import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import articles from '../data/journalArticles';
import { localizeArticle } from '../data/journalArticles.i18n';
import { useT, useLang } from '../i18n/LanguageContext';

const FILTERS = [
  { key: 'all', match: null },
  { key: 'mice', match: 'MICE' },
  { key: 'eventDesign', match: 'EVENT DESIGN' },
  { key: 'weddings', match: 'WEDDINGS' },
  { key: 'destinations', match: 'DESTINATIONS' },
  { key: 'studioNotes', match: 'STUDIO NOTES' },
];

const eyebrowStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '9pt',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6A87A',
};

const splitTitle = (article) => {
  const t = article.title.trimEnd();
  const accent = article.titleAccent;
  if (accent && t.endsWith(accent)) {
    const head = t.slice(0, t.length - accent.length).trimEnd();
    return { head, accent };
  }
  return { head: t, accent: '' };
};

/* ── Article card (used in grid + "More from" section) ── */
const ArticleCard = ({ article: rawArticle }) => {
  const t = useT();
  const { lang } = useLang();
  const article = localizeArticle(rawArticle, lang);
  const meta = `${article.category} · ${article.readMin} ${t('common.minRead').toUpperCase()} · ${article.publishedLabel}`;

  return (
    <Link
      to={`/journal/${article.slug}`}
      data-testid={`journal-card-${article.slug}`}
      className="group block"
    >
      {/* Category chip + copper hairline above photo */}
      <div className="mb-4">
        <div
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '9pt',
            fontWeight: 600,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: '#D6A87A',
            paddingBottom: '8px',
            borderBottom: '1px solid rgba(214,168,122,0.45)',
          }}
        >
          {article.category}
        </div>
      </div>

      {/* Photograph */}
      <div className="overflow-hidden" style={{ aspectRatio: '4/5' }}>
        <img
          src={article.cover}
          alt={article.title}
          onError={(e) => {
            if (article.fallback && e.currentTarget.src !== article.fallback) {
              e.currentTarget.src = article.fallback;
            }
          }}
          className="w-full h-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.02]"
        />
      </div>

      {/* Title + meta */}
      <div className="mt-7">
        <h3
          className="relative inline-block max-w-full"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: '24pt',
            lineHeight: 1.2,
            fontWeight: 500,
            color: '#0A132F',
            letterSpacing: '-0.005em',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {article.title}
        </h3>
        {/* Hover hairline */}
        <div
          className="mt-2 h-px transition-[width] duration-[280ms]"
          style={{
            width: '0',
            backgroundColor: 'rgba(214,168,122,0.65)',
          }}
          data-card-rule
        />
        <style>{`
          [data-testid="journal-card-${article.slug}"]:hover [data-card-rule] {
            width: 56px;
          }
        `}</style>

        <p
          className="mt-3"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '9pt',
            fontWeight: 500,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#4A4F63',
          }}
        >
          {meta}
        </p>
      </div>
    </Link>
  );
};

const Journal = () => {
  const t = useT();
  const { lang } = useLang();
  const [filter, setFilter] = useState('all');

  const featured = localizeArticle(
    articles.find((a) => a.featured) || articles[0],
    lang
  );

  const visibleGrid = useMemo(() => {
    const others = articles.filter((a) => a.slug !== featured.slug);
    if (filter === 'all') return others;
    const match = FILTERS.find((f) => f.key === filter)?.match;
    if (!match) return others;
    return others.filter((a) => a.categoryFilter === match || a.category === match);
  }, [filter, featured]);

  const featuredTitle = splitTitle(featured);

  return (
    <div data-testid="journal-page">
      <SEO
        title="Journal, Notes from the Studio | GlowMeets"
        description="Field reports, design notes, and the occasional argument about florals. The GlowMeets Journal."
        keywords="GlowMeets journal, MICE insights, event design notes, destination wedding writing, hospitality editorial"
        url="/journal"
      />

      {/* ───────────────────────── 1. HERO ───────────────────────── */}
      <section
        className="bg-navy w-full flex items-center justify-center"
        style={{ minHeight: '100vh', paddingTop: '120px', paddingBottom: '90px' }}
        data-testid="journal-hero"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div style={eyebrowStyle} className="mb-7">
            {t('journal.hero.eyebrow')}
          </div>
          <h1
            className="text-bone mx-auto"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: 'clamp(40px, 5vw, 64pt)',
              lineHeight: 1.05,
              fontWeight: 500,
              letterSpacing: '-0.005em',
              maxWidth: '14ch',
            }}
          >
            <span className="block">{t('journal.hero.line1')}</span>
            <span
              className="block italic"
              style={{ color: '#D6A87A', fontWeight: 400 }}
            >
              {t('journal.hero.line2')}
            </span>
          </h1>
          <p
            className="mt-7 mx-auto"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '18pt',
              color: '#D6A87A',
              lineHeight: 1.5,
              maxWidth: '56ch',
            }}
          >
            {t('journal.hero.sub')}
          </p>
          <div className="mt-12 flex justify-center">
            <span
              style={{
                display: 'block',
                width: '1px',
                height: '60px',
                backgroundColor: 'rgba(214,168,122,0.6)',
              }}
            />
          </div>
        </div>
      </section>

      {/* ───────────────────────── 2. STICKY FILTER STRIP ───────────────────────── */}
      <div
        className="sticky top-0 z-40 w-full"
        style={{
          backgroundColor: '#0A132F',
          height: '60px',
          borderBottom: '1px solid rgba(214,168,122,0.35)',
        }}
        data-testid="journal-filter-strip"
      >
        <div className="h-full max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
          <nav className="flex items-center overflow-x-auto whitespace-nowrap" data-testid="journal-filters">
            {FILTERS.map((f, idx) => {
              const active = filter === f.key;
              const tid = f.key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
              return (
                <React.Fragment key={f.key}>
                  <button
                    type="button"
                    onClick={() => setFilter(f.key)}
                    data-testid={`jr-filter-${tid}`}
                    className="transition-all duration-200"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '11pt',
                      fontWeight: 500,
                      letterSpacing: active ? '0.24em' : '0.22em',
                      textTransform: 'uppercase',
                      color: '#D6A87A',
                      borderBottom: active
                        ? '1px solid #D6A87A'
                        : '1px solid transparent',
                      paddingBottom: '4px',
                      background: 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.letterSpacing = '0.24em';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.letterSpacing = active ? '0.24em' : '0.22em';
                    }}
                  >
                    {t(`filters.${f.key}`)}
                  </button>
                  {idx < FILTERS.length - 1 && (
                    <span
                      className="mx-3 select-none"
                      aria-hidden="true"
                      style={{ color: 'rgba(214,168,122,0.45)' }}
                    >
                      ·
                    </span>
                  )}
                </React.Fragment>
              );
            })}
          </nav>
        </div>
      </div>

      {/* ───────────────────────── 3. FEATURED ARTICLE ───────────────────────── */}
      <section
        className="bg-bone"
        style={{ paddingTop: '100px', paddingBottom: '100px' }}
        data-testid="journal-featured"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* LEFT, cover photo (60%) */}
            <Link
              to={`/journal/${featured.slug}`}
              className="lg:col-span-7 block group"
              data-testid="journal-featured-photo"
            >
              <div
                style={{ borderTop: '1px solid rgba(214,168,122,0.35)' }}
                className="pt-2"
              >
                <div className="overflow-hidden" style={{ aspectRatio: '4/5' }}>
                  <img
                    src={featured.cover}
                    alt={featured.title}
                    onError={(e) => {
                      if (
                        featured.fallback &&
                        e.currentTarget.src !== featured.fallback
                      ) {
                        e.currentTarget.src = featured.fallback;
                      }
                    }}
                    className="w-full h-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.02]"
                  />
                </div>
              </div>
            </Link>

            {/* RIGHT, meta + title + dek + byline + link (40%) */}
            <div className="lg:col-span-5 lg:pt-6">
              <div style={eyebrowStyle} className="mb-5">
                {t('journal.featured')} · {featured.category} · {featured.publishedLabel}
              </div>

              <h2
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: 'clamp(28px, 3vw, 38pt)',
                  lineHeight: 1.1,
                  fontWeight: 500,
                  color: '#0A132F',
                  letterSpacing: '-0.005em',
                }}
              >
                {featuredTitle.head}{' '}
                <em
                  className="italic"
                  style={{ color: '#B98556', fontWeight: 400 }}
                >
                  {featuredTitle.accent}
                </em>
              </h2>

              <p
                className="mt-6"
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontStyle: 'italic',
                  fontWeight: 400,
                  fontSize: '16pt',
                  lineHeight: 1.55,
                  color: '#4A4F63',
                  maxWidth: '50ch',
                }}
              >
                {featured.dek}
              </p>

              <div
                className="mt-8"
                style={{
                  height: '1px',
                  width: '64px',
                  backgroundColor: 'rgba(214,168,122,0.6)',
                }}
              />

              <div
                className="mt-6"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '10pt',
                  fontWeight: 600,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: '#D6A87A',
                }}
              >
                {t('common.by')} Ahmet Kazancı · {featured.readMin} {t('common.minRead')}
              </div>

              <Link
                to={`/journal/${featured.slug}`}
                data-testid="journal-featured-cta"
                className="inline-flex items-center mt-7 transition-colors"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '11pt',
                  fontWeight: 600,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: '#D6A87A',
                  border: '1px solid #D6A87A',
                  padding: '10px 22px',
                  borderRadius: '999px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#D6A87A';
                  e.currentTarget.style.color = '#0A132F';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#D6A87A';
                }}
              >
                {t('cta.readPiece')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────── 4. ARTICLE GRID ───────────────────────── */}
      <section
        className="bg-white"
        style={{ paddingTop: '90px', paddingBottom: '90px', paddingLeft: '60px', paddingRight: '60px' }}
        data-testid="journal-grid-section"
      >
        <div
          key={filter}
          className="max-w-[1400px] mx-auto fade-in"
          style={{ animationDuration: '280ms' }}
        >
          {visibleGrid.length === 0 ? (
            <p
              className="text-center"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontSize: '20pt',
                color: '#4A4F63',
              }}
            >
              {t('journal.emptyCategory')}
            </p>
          ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              style={{ gap: '48px 32px' }}
            >
              {visibleGrid.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ───────────────────────── 5. CLOSING BAND ───────────────────────── */}
      <section
        className="bg-navy text-bone w-full text-center"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="journal-closing"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
          <img
            src="/glowmeets-mark.png"
            alt="GlowMeets brand mark"
            style={{ height: '64px', width: 'auto' }}
          />

          <p
            className="mt-7"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '14pt',
              color: 'rgba(244,239,227,0.7)',
              lineHeight: 1.5,
            }}
          >
            {t('journal.closingIndex')}
          </p>

          <Link
            to="/contact"
            data-testid="journal-cta"
            className="inline-flex items-center justify-center mt-8 transition-colors"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '13pt',
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#D6A87A',
              backgroundColor: 'transparent',
              border: '1px solid #D6A87A',
              padding: '0 28px',
              borderRadius: '999px',
              minWidth: '280px',
              height: '56px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#D6A87A';
              e.currentTarget.style.color = '#0A132F';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#D6A87A';
            }}
          >
            {t('cta.bookConsultation')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Journal;
export { ArticleCard };
